import { useState } from 'react';

const phase1Nodes = [
  {
    id: 'webhook',
    label: 'OTP 邀請碼核發',
    tech: '授權網關',
    description:
      '申請者提交 Email 後，系統自動核發單次授權邀請碼（OTP）至指定信箱。每組 OTP 具備時效性與唯一性，不可轉讓、不可重複使用。',
    status: 'AUTH',
    color: '#00D9FF',
  },
  {
    id: 'whitelist',
    label: '白名單寫入',
    tech: '加密資料庫',
    description:
      '系統自動將授權 Email 寫入加密資料庫，為後續運算資源的訪問建立授權憑證。未通過驗證的請求將在 Node 02 被立即終止。',
    status: 'LOCK',
    color: '#00FF88',
  },
];

const phase2Nodes = [
  {
    id: 'node-01',
    num: '01',
    label: '數據攝取',
    tech: '加密表單',
    description: '從授權申請者處安全採集精確的出生時間座標與地理錨點，作為底層運算的原始輸入變量。',
    status: 'READY',
    color: '#00D9FF',
  },
  {
    id: 'node-02',
    num: '02',
    label: '零信任驗證',
    tech: '授權網關',
    description: '將攝取的 Email 與加密資料庫進行交叉比對。若無匹配記錄，流水線立即終止，不生成任何部分輸出。',
    status: 'VERIFY',
    color: '#FF4D4D',
  },
  {
    id: 'node-03',
    num: '03',
    label: '時空座標校正',
    tech: '座標解析器',
    description: '基於地理座標進行高精度真太陽時修正，消除標準時區與實際天文時間之間的系統性偏差。',
    status: 'EXEC',
    color: '#FFD700',
  },
  {
    id: 'node-04',
    num: '04',
    label: '多維矩陣運算',
    tech: 'Deno 確定性核心',
    description: 'Deno 確定性核心將校正後的時間變量轉換為原始多維參數矩陣，供下游處理器使用。',
    status: 'EXEC',
    color: '#00D9FF',
  },
  {
    id: 'node-05',
    num: '05',
    label: 'AI 戰略轉譯',
    tech: 'GPT-4o 編譯器',
    description: '專有提示引擎將原始矩陣轉化為行為經濟學與戰術行動指令。GPT-4o 在此嚴格扮演「編譯器」角色，而非生成器。',
    status: 'PROC',
    color: '#00FF88',
  },
  {
    id: 'node-06',
    num: '06',
    label: 'JSON 結構化強制',
    tech: '格式強制器',
    description: '將非結構化 AI 輸出強制映射至剛性 JSON Schema，確保數據完整性與下游兼容性。任何偏差均觸發重試機制。',
    status: 'PROC',
    color: '#FFD700',
  },
  {
    id: 'node-07',
    num: '07',
    label: 'PDF 戰略報告生成',
    tech: '報告渲染引擎',
    description: '將驗證後的 JSON 數據映射至結構化 PDF 模板，生成包含 6 大矩陣、12 個月高頻預警圖的完整戰略報告。',
    status: 'RENDER',
    color: '#00D9FF',
  },
  {
    id: 'node-08',
    num: '08',
    label: '訪問鏈接生成',
    tech: '安全交付',
    description: '為授權用戶生成具備適當權限的安全可分享鏈接，確保報告僅對授權 Email 持有者可訪問。',
    status: 'SEAL',
    color: '#00FF88',
  },
  {
    id: 'node-09',
    num: '09',
    label: '加密交付',
    tech: 'SMTP / TLS',
    description: '通過加密傳輸層，將 PDF 報告自動發送至授權 Email 地址。全程無人工介入，交付時間 5 分鐘以內。',
    status: 'SEND',
    color: '#FFD700',
  },
  {
    id: 'node-10',
    num: '10',
    label: '狀態鎖定',
    tech: 'Anti-Replay Guard',
    description: '系統將資料庫記錄更新為 [已交付] 狀態，鎖定令牌以防止重複提交與未授權訪問。流水線終止。',
    status: 'LOCKED',
    color: '#FF4D4D',
  },
];

function StatusDot({ color }: { color: string }) {
  return (
    <span
      className="inline-block w-2 h-2 rounded-full flex-shrink-0"
      style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}` }}
    />
  );
}

function TechBadge({ label }: { label: string }) {
  return (
    <span className="font-mono text-[10px] tracking-widest px-2 py-0.5 rounded border border-white/10 text-white/40 bg-white/5 whitespace-nowrap">
      {label}
    </span>
  );
}

export default function SystemLogicPipeline() {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  return (
    <section id="system-logic" className="bg-[#050508] text-white py-24 px-6 md:px-12 border-t border-white/5 relative">
      {/* Grid texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="mb-20">
          <div className="flex items-center gap-4 mb-5">
            <div className="h-px w-12 bg-[#00D9FF]/60" />
            <span className="font-mono text-[11px] tracking-[0.3em] text-[#00D9FF] uppercase">
              // System_Architecture
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-white">
            邏輯流水線
          </h2>
          <p className="text-sm text-white/40 font-mono max-w-xl leading-relaxed">
            完全解耦的零信任運算鏈。每個節點相互隔離、可審計，並按序列門控執行。
          </p>
        </div>

        {/* ── PHASE 1 ── */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-8">
            <span className="font-mono text-[10px] tracking-[0.25em] px-3 py-1 rounded-full border border-[#00D9FF]/30 text-[#00D9FF] bg-[#00D9FF]/5">
              PHASE 01
            </span>
            <span className="text-base font-semibold tracking-wide text-white">
              異步授權 &amp; 安全鎖定
            </span>
          </div>

          <div className="border border-white/8 rounded-lg bg-white/[0.02] p-8 space-y-0">
            {phase1Nodes.map((node, idx) => (
              <div key={node.id}>
                <div
                  className="flex gap-5 group cursor-default transition-all duration-200"
                  onMouseEnter={() => setHoveredNode(node.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                >
                  {/* Left: connector */}
                  <div className="flex flex-col items-center w-8 flex-shrink-0">
                    <div
                      className="w-3 h-3 rounded-full border-2 flex-shrink-0 transition-all duration-300"
                      style={{
                        borderColor: node.color,
                        backgroundColor: hoveredNode === node.id ? node.color : 'transparent',
                        boxShadow: hoveredNode === node.id ? `0 0 10px ${node.color}` : 'none',
                      }}
                    />
                    {idx < phase1Nodes.length - 1 && (
                      <div className="w-px flex-1 bg-white/10 my-1" style={{ minHeight: 32 }} />
                    )}
                  </div>

                  {/* Right: content */}
                  <div className="pb-8 flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="font-mono text-sm font-bold tracking-widest" style={{ color: node.color }}>
                        {node.label}
                      </span>
                      <TechBadge label={node.tech} />
                      <div className="flex items-center gap-1.5 ml-auto">
                        <StatusDot color={node.color} />
                        <span className="font-mono text-[10px] tracking-widest text-white/30">
                          {node.status}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-white/50 leading-relaxed">{node.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Phase connector arrow */}
        <div className="flex flex-col items-center mb-12">
          <div className="w-px h-8 bg-white/10" />
          <div className="font-mono text-[10px] tracking-[0.2em] text-white/20 my-2">
            PIPELINE_HANDOFF
          </div>
          <div className="w-px h-8 bg-white/10" />
          <div
            className="w-0 h-0"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '8px solid rgba(255,255,255,0.15)',
            }}
          />
        </div>

        {/* ── PHASE 2 ── */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className="font-mono text-[10px] tracking-[0.25em] px-3 py-1 rounded-full border border-[#00FF88]/30 text-[#00FF88] bg-[#00FF88]/5">
              PHASE 02
            </span>
            <span className="text-base font-semibold tracking-wide text-white">
              10 節點解耦運算流水線
            </span>
          </div>
          <p className="font-mono text-[11px] text-white/25 tracking-widest mb-8 pl-1">
            BLACKBOX / FULLY_AUTOMATED / ZERO_HUMAN_INTERVENTION
          </p>

          <div className="border border-white/8 rounded-lg bg-white/[0.02] p-8">
            {phase2Nodes.map((node, idx) => (
              <div key={node.id}>
                <div
                  className="flex gap-5 group cursor-default transition-all duration-200"
                  onMouseEnter={() => setHoveredNode(node.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                >
                  {/* Left: number + connector */}
                  <div className="flex flex-col items-center w-8 flex-shrink-0">
                    <div
                      className="w-7 h-7 rounded flex-shrink-0 flex items-center justify-center border transition-all duration-300"
                      style={{
                        borderColor: hoveredNode === node.id ? node.color : 'rgba(255,255,255,0.1)',
                        backgroundColor: hoveredNode === node.id ? `${node.color}15` : 'transparent',
                      }}
                    >
                      <span
                        className="font-mono text-[10px] font-bold"
                        style={{ color: hoveredNode === node.id ? node.color : 'rgba(255,255,255,0.3)' }}
                      >
                        {node.num}
                      </span>
                    </div>
                    {idx < phase2Nodes.length - 1 && (
                      <div className="w-px flex-1 bg-white/8 my-1" style={{ minHeight: 28 }} />
                    )}
                  </div>

                  {/* Right: content */}
                  <div className="pb-7 flex-1">
                    <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                      <span
                        className="font-mono text-[13px] font-bold tracking-wider transition-colors duration-200"
                        style={{ color: hoveredNode === node.id ? node.color : '#ffffff' }}
                      >
                        {node.label}
                      </span>
                      <TechBadge label={node.tech} />
                      <div className="flex items-center gap-1.5 ml-auto">
                        <StatusDot color={node.color} />
                        <span className="font-mono text-[10px] tracking-widest text-white/25">
                          {node.status}
                        </span>
                      </div>
                    </div>
                    <p className="text-[13px] text-white/45 leading-relaxed">{node.description}</p>
                  </div>
                </div>
              </div>
            ))}

            {/* Terminal state */}
            <div className="mt-4 pt-6 border-t border-white/8 flex items-center gap-3">
              <div className="w-8 flex-shrink-0 flex justify-center">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: '#FF4D4D', boxShadow: '0 0 8px #FF4D4D' }}
                />
              </div>
              <span className="font-mono text-[11px] tracking-[0.3em] text-white/30">
                PIPELINE_TERMINATED — TOKEN_LOCKED — STATE: [DELIVERED]
              </span>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-10 flex items-start gap-3 border border-white/6 rounded px-5 py-4 bg-white/[0.015]">
          <span className="font-mono text-[10px] text-[#00D9FF]/60 tracking-widest mt-0.5 flex-shrink-0">
            NOTE
          </span>
          <p className="font-mono text-[11px] text-white/25 leading-relaxed tracking-wide">
            所有節點按嚴格順序執行。Node 02 的任何驗證失敗均導致流水線立即終止。不在授權渠道外生成或存儲任何部分輸出。
          </p>
        </div>
      </div>
    </section>
  );
}
